import React, { Suspense } from 'react';
import { Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import { Spinner } from '@infotrack/zenith-ui';

import { RequireAuth } from '@/auth/requireAuth';
import { RequirePermission } from '@/auth/requirePermission';
import { Login } from '@/auth/components';
import { DashboardPage } from '@/features/dashboard';
import { TopFivePage, TopFiveDetailPage, ImportPage, AddUpdateTopFivePage } from '@/features/topfive';
import { StaffPage, StaffDetailsPage } from '@/features/staff';
import { BonusesPage } from '@/features/bonuses';

const PageLoadingFallback: React.FC = () => <Spinner fullSize />;

const Routes: React.FC = () => {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <RouterRoutes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <DashboardPage />
            </RequireAuth>
          }
        />

        <Route
          path="/topfive"
          element={
            <RequireAuth>
              <TopFivePage />
            </RequireAuth>
          }
        />

        <Route
          path="/topfive/:topFiveId"
          element={
            <RequireAuth>
              <TopFiveDetailPage />
            </RequireAuth>
          }
        />

        {/* Staff management route */}
        <Route
          path="/staff"
          element={
            <RequireAuth>
              <RequirePermission permission={['hasiNominateAccess', 'hasiTopFiveStaffEditorAccess']}>
                <StaffPage />
              </RequirePermission>
            </RequireAuth>
          }
        />

        <Route
          path="/staff/:id"
          element={
            <RequireAuth>
              <RequirePermission permission={['hasiNominateAccess', 'hasiTopFiveStaffEditorAccess']}>
                <StaffDetailsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />

        {/* Calculate Bonuses route */}
        <Route
          path="/bonuses"
          element={
            <RequireAuth>
              <RequirePermission permission="hasCalculateTopFiveBonusesAccess">
                <BonusesPage />
              </RequirePermission>
            </RequireAuth>
          }
        />

        {/* Import route - CSV bulk import */}
        <Route
          path="/import"
          element={
            <RequireAuth>
              <RequirePermission permission="hasAddTopFiveAccess">
                <ImportPage />
              </RequirePermission>
            </RequireAuth>
          }
        />

        {/* Add/Update TopFive route - Individual staff TopFive management */}
        <Route
          path="/topfive/add"
          element={
            <RequireAuth>
              <RequirePermission permission="hasAddTopFiveAccess">
                <AddUpdateTopFivePage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        {/* Legacy route redirects */}
        <Route path="/home" element={<Navigate to="/topfive" replace />} />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/topfive" replace />} />

        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/topfive" replace />} />
      </RouterRoutes>
    </Suspense>
  );
};

export default Routes;
